import {
  BarChartRounded,
  Circle,
  HorizontalRuleRounded,
  MoreHorizRounded,
  ShowChartRounded,
} from "@mui/icons-material";
import { Box, Button, Menu, Stack, TextField, ToggleButtonGroup } from "@mui/material";
import { useState } from "react";
import { BlockPicker } from "react-color";
import { Controller, useFieldArray, UseFormReturn } from "react-hook-form";
import { v4 as newGuid } from "uuid";
import { DraggableList } from "..";
import { GRAPH_COLORS } from "../../constants";
import { lexorank } from "../../utilities";
import { TooltipToggleButton } from "../TooltipToggleButton";
import { ICreateEditMetricFormState } from "./CreateEditMetricForm";

interface IProps {
  form: UseFormReturn<ICreateEditMetricFormState>;
}

const EditMetricDatasets = (props: IProps) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedDatasetIndex, setSelectedDatasetIndex] = useState<number>(0);
  const { fields, append, remove } = useFieldArray({ name: "datasets", control: props.form.control });

  function handleAddDataset() {
    append({
      guid: newGuid(),
      title: "",
      displayAsBar: false,
      color: GRAPH_COLORS[2],
      dotted: false,
      data: [],
      sortOrder: lexorank.getNextRank(fields),
    });
  }

  function handleReorder(sourceIndex: number, destinationIndex: number) {
    props.form.setValue(
      `datasets.${sourceIndex}.sortOrder`,
      lexorank.getRank(props.form.watch("datasets"), sourceIndex, destinationIndex)
    );
  }

  function handleLineStyleChanged(value: boolean, datasetIndex: number) {
    if (value != null) {
      props.form.setValue(`datasets.${datasetIndex}.dotted`, value);
    }
  }

  function handleTypeChanged(value: boolean, datasetIndex: number) {
    if (value != null) {
      props.form.setValue(`datasets.${datasetIndex}.displayAsBar`, value);
    }
  }

  return (
    <>
      <Stack spacing={1}>
        <DraggableList
          id="datasets"
          dataSource={props.form.watch("datasets").sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))}
          renderItem={(item, index) => (
            <Stack direction="row" key={item.guid} spacing={1} sx={{ p: 0.25, alignItems: "center" }}>
              <Controller
                name={`datasets.${index}.title`}
                control={props.form.control}
                rules={{ required: { message: "Title is required", value: true } }}
                render={({ field }) => <TextField {...field} />}
              />
              <Button
                variant="text"
                size="small"
                onClick={(event) => {
                  setMenuAnchor(event.currentTarget);
                  setSelectedDatasetIndex(index);
                }}
                sx={{ minWidth: 0 }}
                disableRipple
              >
                <Controller
                  name={`datasets.${index}.color`}
                  control={props.form.control}
                  render={({ field }) => <Circle sx={{ color: field.value }} fontSize="large" />}
                />
              </Button>
              <Controller
                name={`datasets.${index}.displayAsBar`}
                control={props.form.control}
                render={({ field }) => (
                  <ToggleButtonGroup
                    value={field.value}
                    color="primary"
                    size="small"
                    exclusive
                    onChange={(_, value: boolean) => handleTypeChanged(value, index)}
                  >
                    <TooltipToggleButton title="Line" value={false}>
                      <ShowChartRounded />
                    </TooltipToggleButton>

                    <TooltipToggleButton title="Bar" value={true}>
                      <BarChartRounded />
                    </TooltipToggleButton>
                  </ToggleButtonGroup>
                )}
              />
              <Controller
                name={`datasets.${index}.dotted`}
                control={props.form.control}
                render={({ field }) => (
                  <ToggleButtonGroup
                    value={field.value}
                    color="primary"
                    size="small"
                    exclusive
                    onChange={(e, value: boolean) => handleLineStyleChanged(value, index)}
                    disabled={props.form.watch(`datasets.${index}.displayAsBar`)}
                  >
                    <TooltipToggleButton title="Solid Line" value={false}>
                      <HorizontalRuleRounded />
                    </TooltipToggleButton>

                    <TooltipToggleButton title="Dotted Line" value={true}>
                      <MoreHorizRounded />
                    </TooltipToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Stack>
          )}
          onReorder={handleReorder}
          onDelete={(index) => remove(index)}
        />
        <Box>
          <Button variant="tertiary" onClick={handleAddDataset} sx={{ alignSelf: "start", ml: 1 }}>
            Add Critical Metric
          </Button>
        </Box>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        MenuListProps={{ sx: { p: 0 } }}
      >
        <Controller
          name={`datasets.${selectedDatasetIndex}.color`}
          control={props.form.control}
          render={({ field }) => (
            <BlockPicker
              color={field.value}
              colors={GRAPH_COLORS}
              triangle="hide"
              onChangeComplete={(color) => props.form.setValue(`datasets.${selectedDatasetIndex}.color`, color.hex)}
            />
          )}
        />
      </Menu>
    </>
  );
};

export { EditMetricDatasets };
